import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LoadingService {
  private activeCount = 0;
  private readonly SHOW_DELAY_MS = 50;
  private readonly MIN_VISIBLE_MS = 500;
  private readonly isActive$ = new BehaviorSubject<boolean>(false);
  public readonly loading$: Observable<boolean>;

  /**
   * Observable that emits true when the loading state is active,
   * and emits false when it is inactive.
   */
  constructor() {
    this.loading$ = this.isActive$.pipe(
      // When the loading state turns on, wait for a short delay before emitting true.
      // This is to avoid flicker when the loading state rapidly turns on and off.
      switchMap(active => {
        if (active) {
          return timer(this.SHOW_DELAY_MS).pipe(
            // Make sure the loading state is still active when the timer goes off.
            withLatestFrom(this.isActive$),
            filter(([, stillActive]) => stillActive),
            map(() => true)
          );
        } else {
          return timer(this.MIN_VISIBLE_MS).pipe(map(() => false));
        }
      }),
      // Start with false, so that the first emission is false.
      startWith(false),
      // Share the observable so that all subscribers receive the same sequence of values.
      // This is important because the loading state is used in multiple components.
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Updates the loading state based on the provided boolean value.
   * This function is intended to be used in a HTTP interceptor to set the loading state.
   * @param loading true if the loading state should be set to active, false otherwise.
   * @param _url the URL of the request that triggered the loading state change.
   * This parameter is not currently used.
   */
  public setLoading(loading: boolean, _url: string): void {
    // If the loading state is already active, just increment the active count.
    if (loading) {
      this.activeCount++;
      // If this is the first request to set the loading state to active, set the observable to true.
      if (this.activeCount === 1) this.isActive$.next(true);
    } else {
      // If the loading state is not active, decrement the active count.
      // If the active count is 0, set the observable to false.
      if (this.activeCount > 0) {
        this.activeCount--;
        if (this.activeCount === 0) this.isActive$.next(false);
      }
    }
  }
}
